import { z } from 'zod';
import { customerStatusSchema, customerTypeSchema } from './customer.schema';

/**
 * Ausdrucke / Berichte im CRM (2026-09-08, Wunsch Felix): benannte
 * Standardabfragen über Kunden und ihre Ansprechpartner — ein Klick, ein Land
 * wählen, fertig. Kein Filter-Baukasten (den gibt es im Selektionspool,
 * ADR 0046), sondern eine **feste Liste** kuratierter Berichte.
 *
 * Drei Entscheidungen, die den ganzen Zuschnitt tragen:
 *
 * 1. **Eine flache Zeilenform für alle Berichte.** Jede Zeile ist ein Paar
 *    „Kunde + (optional) Ansprechpartner". Damit haben Tabelle, Excel, CSV und
 *    PDF genau ein Format, und „Kunden ohne Ansprechpartner" ist derselbe
 *    Datensatz mit leeren Personenfeldern — statt drei Sonderformen.
 * 2. **Alles, was passt.** Es gibt keine Seitengröße; der Bericht liefert jede
 *    passende Zeile. `limit`/`isTruncated` sind ein Sicherheitsnetz gegen einen
 *    versehentlich filterlosen Aufruf über Hunderttausende Zeilen — wird es
 *    erreicht, sagt die Oberfläche das im Wortlaut, statt stillschweigend zu
 *    kürzen.
 * 3. **Land als ISO-2 ODER Klartext.** In den Altdaten steht beides („MX" und
 *    „Mexiko"); der Server prüft deshalb gegen beide Spalten — dieselbe Regel
 *    wie im Selektionspool.
 */

/** Die verfügbaren Standardberichte (feste Liste, kein Nutzer-Speicher). */
export const CRM_REPORT_KEYS = [
  'customer-contacts',
  'contacts-with-email',
  'customers-without-contacts',
] as const;
export const crmReportKeySchema = z.enum(CRM_REPORT_KEYS);
export type CrmReportKey = z.infer<typeof crmReportKeySchema>;

/**
 * Land-Filter: ISO-2 („MX") oder Klartext („Mexiko"). Serverseitig wird der
 * ISO-Code exakt und der Klartext als „enthält" verglichen (case-insensitive) —
 * so trifft „Mexiko" auch „Mexiko (MEX)" aus dem Import.
 */
export const crmReportCountrySchema = z.string().trim().min(1).max(60);

export const crmReportQuerySchema = z.object({
  report: crmReportKeySchema,
  /** Land des KUNDEN — Ansprechpartner haben keine eigene Adresse. */
  country: crmReportCountrySchema.optional(),
  /** Kunde / Interessent / Lieferant; ohne Angabe alle Typen. */
  type: customerTypeSchema.optional(),
  /**
   * Ohne Angabe nur aktive Geschäftspartner: Ein Ausdruck ist eine
   * Arbeitsliste, und archivierte Kunden gehören nicht in ein Mailing.
   * `status=INACTIVE` liefert ausdrücklich nur die inaktiven.
   */
  status: customerStatusSchema.optional(),
  /** Nur Hauptansprechpartner je Kunde (wirkt nur auf Personen-Berichte). */
  onlyPrimary: z.coerce.boolean().optional(),
});
export type CrmReportQuery = z.infer<typeof crmReportQuerySchema>;

/**
 * Eine Ergebniszeile: Kundendaten + (optional) die Person. Bewusst flach und
 * mit wiederholten Kundenspalten — genau so lässt sich in Excel filtern,
 * sortieren und ein Serienbrief anhängen.
 */
export const crmReportRowSchema = z.object({
  customerId: z.string().uuid(),
  customerNumber: z.string().nullable(),
  customerName: z.string(),
  customerType: customerTypeSchema,
  customerStatus: customerStatusSchema,
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  /** ISO-2 wie erfasst. */
  country: z.string().nullable(),
  /** Land im Klartext (Import-Feld) — im Ausdruck die sprechende Angabe. */
  countryName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  customerPhone: z.string().nullable(),
  vatId: z.string().nullable(),
  industry: z.string().nullable(),

  /** Ab hier die Person; alles null, wenn der Kunde keinen Ansprechpartner hat. */
  contactId: z.string().uuid().nullable(),
  salutation: z.string().nullable(),
  title: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  position: z.string().nullable(),
  department: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  contactMobile: z.string().nullable(),
  contactLanguage: z.string().nullable(),
  isPrimary: z.boolean(),
});
export type CrmReportRow = z.infer<typeof crmReportRowSchema>;

export const crmReportResultSchema = z.object({
  report: crmReportKeySchema,
  generatedAt: z.string().datetime(),
  /** Die angewandten Filter, wie der Server sie gelesen hat (für Kopfzeile und PDF). */
  filters: z.object({
    country: z.string().nullable(),
    type: customerTypeSchema.nullable(),
    status: customerStatusSchema.nullable(),
    onlyPrimary: z.boolean(),
  }),
  rows: z.array(crmReportRowSchema),
  /** Zeilen = Kunde-Person-Paare; Kunden/Personen sind die eindeutigen Zählungen. */
  rowCount: z.number().int(),
  customerCount: z.number().int(),
  contactCount: z.number().int(),
  /** Sicherheitsnetz: Zeilen wurden bei `limit` abgeschnitten (Filter setzen!). */
  isTruncated: z.boolean(),
  limit: z.number().int(),
});
export type CrmReportResult = z.infer<typeof crmReportResultSchema>;

/**
 * Obergrenze der Zeilen je Bericht. Großzügig über dem Bestand (rund 4.200
 * Kunden), aber endlich: ohne Deckel legt ein filterloser Aufruf irgendwann
 * Server und Browser lahm.
 */
export const CRM_REPORT_ROW_LIMIT = 50_000;

/** Beschriftung + Erklärung je Bericht — eine Quelle für Seite, PDF und Doku. */
export const CRM_REPORT_META: Record<
  CrmReportKey,
  { label: string; purpose: string; includes: string; parameters: readonly ('country' | 'type' | 'status' | 'onlyPrimary')[] }
> = {
  'customer-contacts': {
    label: 'Kunden + Ansprechpartner',
    purpose: 'Adressliste eines Landes mit allen Personen — für Reise, Mailing und Telefonrunde.',
    includes:
      'Jeder passende Kunde mit jedem seiner Ansprechpartner, eine Zeile je Person. Kunden OHNE Ansprechpartner stehen mit leeren Personenspalten in der Liste, damit „alle Kunden" wirklich alle sind.',
    parameters: ['country', 'type', 'status', 'onlyPrimary'],
  },
  'contacts-with-email': {
    label: 'Ansprechpartner mit E-Mail',
    purpose: 'Verteiler für Serienmails und Einladungen.',
    includes:
      'Nur Personen mit hinterlegter E-Mail-Adresse, eine Zeile je Person. Kunden ohne Ansprechpartner oder ohne Adresse fehlen hier bewusst.',
    parameters: ['country', 'type', 'status', 'onlyPrimary'],
  },
  'customers-without-contacts': {
    label: 'Kunden ohne Ansprechpartner',
    purpose: 'Datenlücken schließen: Wen kennen wir, ohne jemanden zu kennen?',
    includes: 'Kunden, bei denen kein (nicht gelöschter) Ansprechpartner hinterlegt ist — eine Zeile je Kunde, Personenspalten leer.',
    parameters: ['country', 'type', 'status'],
  },
};
